import { Injectable } from "@angular/core";
import { map, Observable } from "rxjs";
import { HttpParams } from "@angular/common/http";
import { ServerDataSource } from "angular2-smart-table";
import { environment } from "../../../environments/environment";
import { HttpService } from "./common/http.service";
import { User } from "../interfaces/auth/users";
import { omit } from "lodash";
import { defaultDataOmitFields, prepRecordForApi } from "../utils/utils.service";
import { TrainingResult, TrainingResultData } from "../interfaces/training";

@Injectable()
export class TrainingResultService extends TrainingResultData {

  private readonly url: string;

  get gridDataSource(): ServerDataSource {
    return this.api.getServerDataSource(`${environment.API_BASE_URL}/${this.url}?populate=*`);
  }

  get gridArchivedDataSource(): ServerDataSource {
    return this.api.getServerDataSource(`${environment.API_BASE_URL}/${this.url}?filters[archivedOn][$notNull]=true`);
  }

  constructor(private api: HttpService) {
    super();
    this.url = 'training-results';
  }

  getDataSourceOfClient(clientId: number): ServerDataSource {
    return this.api.getServerDataSource(`${environment.API_BASE_URL}/${this.url}?with=deep,2&clients_include_id=${clientId}`);
  }

  listAll(): Observable<TrainingResult[]> {
    let params: HttpParams = new HttpParams()
      .append('limit', '-1')
      .append('offset', '0')
      .append('with', 'deep,2');

    return this.api.get(this.url, { params }).pipe(map(res => res.data || res));
  }

  list(pageNumber: number, pageSize: number, query: string = ''): Observable<TrainingResult[]> {
    let params: HttpParams = new HttpParams()
      .append('with', 'deep,2');

    if (pageSize > 0) {
      params = params.append('limit', `${pageSize}`);
    }

    if (pageNumber > 0) {
      params = params.append('offset', `${(pageNumber - 1) * pageSize}`);
    }

    if (query) {
      params = params.append('where', query);
    }

    return this.api.get(this.url, { params }).pipe(map(res => res.data || res));
  }

  rawQry(pageNumber: number, pageSize: number, params: HttpParams):Observable<TrainingResult[] | any> {
    if (pageSize > 0) {
      params = params.append('limit', `${pageSize}`);
    }

    if (pageNumber > 0) {
      params = params.append('offset', `${(pageNumber - 1) * pageSize}`);
    }

    // 'with' for population, 'where' for filters, 'order' for sorting

    return this.api.get(this.url, { params }).pipe(map(res => res.data || res));
  }

  get(id: any): Observable<TrainingResult> {
    return this.api.get(`${this.url}/${id}`, { params: new HttpParams().append('populate', '*') })
      .pipe(map(res => res.data || res));
  }

  create(trainingResult: TrainingResult): Observable<TrainingResult> {
    let params: HttpParams = new HttpParams()
      .append('populate', '*');

    trainingResult = prepRecordForApi(trainingResult);
    return this.api.post(this.url, { data: trainingResult }, params)
      .pipe(map(res => res.data));
  }

  update(TrainingResult: TrainingResult): Observable<TrainingResult> {
    let params: HttpParams = new HttpParams()
      .append('populate', '*');
    const changedTrainingResult = prepRecordForApi(omit(TrainingResult, [...defaultDataOmitFields]));
    return this.api.put(`${this.url}/${TrainingResult.documentId}`, { data: changedTrainingResult }, params)
      .pipe(map(res => res.data));
  }

  archive(TrainingResult: TrainingResult, date: Date, user: User): Observable<TrainingResult> {
    return this.api.put(`${this.url}/${TrainingResult.documentId}`, {
      data: {
        archivedOn: date,
        archivedBy: user,
      },
    });
  }

  unarchive(TrainingResult: TrainingResult): Observable<TrainingResult> {
    return this.api.put(`${this.url}/${TrainingResult.documentId}`, {
      data: {
        archivedOn: null,
        archivedBy: null,
      },
    });
  }

  delete(id: any): Observable<any> {
    return this.api.delete(`${this.url}/${id}`);
  }
}